import React, {useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  Button, Checkbox, Container, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControlLabel, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Loader from '../components/Loader';
import {baseAxios} from '../jwtInterceptor';

const LINE_URL = 'https://line.me/R/ti/p/@240yzqkh';
const CONTACT_URL = 'https://enclopedia.jp/contact/';

const CONDITION_LABELS = {
  S: '新品・タグ付き',
  A: '未使用に近い',
  B: '目立った傷や汚れなし',
  C: '多少の傷や汚れ・使用感あり',
  D: '傷や汚れあり',
  E: '使用が難しい状態',
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(12),
    marginBottom: theme.spacing(12),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(8),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {fontSize: '1.8rem', marginBottom: theme.spacing(3)},
  },
  information: {lineHeight: 1.9},
  guidance: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary[100],
    borderRadius: 5,
  },
  summary: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 5,
  },
  summaryGrid: {display: 'flex', justifyContent: 'space-around', gap: theme.spacing(2)},
  summaryItem: {textAlign: 'center', flex: 1},
  summaryLabel: {color: theme.palette.text.secondary, marginBottom: theme.spacing(0.5)},
  summaryValue: {fontWeight: 'bold'},
  desktopTable: {[theme.breakpoints.down('sm')]: {display: 'none'}},
  mobileItems: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {display: 'block'},
  },
  itemCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 8,
  },
  selectedCard: {borderColor: theme.palette.primary[500], backgroundColor: theme.palette.primary[50]},
  itemHeader: {display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2)},
  itemName: {fontWeight: 'bold'},
  itemPrice: {fontWeight: 'bold', whiteSpace: 'nowrap'},
  itemDetail: {marginTop: theme.spacing(1), color: theme.palette.text.secondary},
  itemDecision: {marginTop: theme.spacing(1)},
  button: {marginTop: theme.spacing(3), paddingTop: theme.spacing(1.5), paddingBottom: theme.spacing(1.5)},
  message: {whiteSpace: 'pre-line', marginTop: theme.spacing(2)},
  completion: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: theme.palette.primary[100],
    borderRadius: 8,
  },
  completionTitle: {fontWeight: 'bold', marginBottom: theme.spacing(1.5)},
  contactButtons: {display: 'flex', gap: theme.spacing(2), marginTop: theme.spacing(3)},
}));

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('ja-JP', {year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date(value))
  : '-';

const conditionLabel = (condition) => (
  CONDITION_LABELS[condition] ? `${condition}（${CONDITION_LABELS[condition]}）` : condition
);

export default function AppraisalResultScreen() {
  const classes = useStyles();
  const {id} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') || '';
  const [result, setResult] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');

  useEffect(() => {
    let active = true;
    baseAxios.get(`/api/inventory/purchase/${id}/appraisal/`, {params: token ? {token} : {}})
      .then((response) => {
        if (!active || !response) return;
        setResult(response.data);
        setDecisions(Object.fromEntries(response.data.items.map((item) => [String(item.id), item.bought])));
      })
      .catch((requestError) => {
        if (!active) return;
        const detail = requestError.response?.data?.detail || '';
        const accountLoginRequired = (
          requestError.response?.status === 403
          && detail.includes('申込時のアカウントでログインしてください')
        );
        if (accountLoginRequired) {
          const appraisalPath = `${location.pathname}${location.search}`;
          navigate(`/login?redirect=${encodeURIComponent(appraisalPath)}`, {replace: true});
          return;
        }
        if (requestError.response?.status === 403 && token) {
          setErrorType('expired');
          setError('この査定結果URLは無効、または有効期限が切れています。');
        } else {
          setErrorType('general');
          setError(detail || '査定結果を取得できませんでした。時間をおいて再度お試しください。');
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, token, location.pathname, location.search, navigate]);

  const selectedItems = useMemo(() => (
    result ? result.items.filter((item) => decisions[String(item.id)]) : []
  ), [result, decisions]);
  const totalCount = selectedItems.length;
  const returnCount = result ? result.items.length - totalCount : 0;
  const totalPrice = selectedItems.reduce((sum, item) => sum + Number(item.price || 0), 0);

  const submitDecision = async () => {
    if (!result || result.decision_finalized || submitting) return;
    setConfirmOpen(false);
    setSubmitting(true);
    setError('');
    try {
      const response = await baseAxios.post(
        `/api/inventory/purchase/${id}/appraisal/decision/`,
        {
          token,
          items: result.items.map((item) => ({id: item.id, bought: Boolean(decisions[String(item.id)])})),
        },
        {params: token ? {token} : {}},
      );
      if (!response) throw new Error('response unavailable');
      setResult((current) => ({
        ...current,
        state: response.data.state,
        seller_decided_at: response.data.seller_decided_at,
        decision_finalized: true,
      }));
      window.scrollTo({top: 0, behavior: 'smooth'});
    } catch (requestError) {
      setError(requestError.response?.data?.detail || '売却回答を確定できませんでした。');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDecision = (item) => (
    <FormControlLabel
      control={<Checkbox
        color="primary"
        checked={Boolean(decisions[String(item.id)])}
        disabled={result.decision_finalized}
        onChange={(event) => setDecisions((current) => ({
          ...current, [String(item.id)]: event.target.checked,
        }))}
      />}
      label={decisions[String(item.id)] ? '売却する' : '返却する'}
    />
  );

  if (loading) return <div className={classes.root}><Loader /></div>;
  if (error && !result) return (
    <Container maxWidth="sm" className={classes.root}>
      <Typography variant="h5">査定結果を表示できません</Typography>
      <Typography className={classes.message}>{error}</Typography>
      {errorType === 'expired' ? (
        <>
          <Typography className={classes.message}>
            売却回答がお済みでない場合は、Enclopedia運営までLINEまたはメールでお問い合わせください。
          </Typography>
          <div className={classes.contactButtons}>
            <Button fullWidth variant="contained" color="primary" component="a" href={LINE_URL} target="_blank" rel="noopener noreferrer">LINEで問い合わせる</Button>
            <Button fullWidth variant="outlined" component="a" href={CONTACT_URL}>メールで問い合わせる</Button>
          </div>
        </>
      ) : (
        <Button className={classes.button} variant="contained" color="primary" onClick={() => window.location.reload()}>再読み込みする</Button>
      )}
    </Container>
  );

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Typography variant="h1" component="h1" align="center" className={classes.title}>買取査定結果</Typography>
      <div className={classes.information}>
        <Typography>買取申込番号：{result.identifier || result.purchase_id}</Typography>
        <Typography>買取申込日：{formatDate(result.created_at)}</Typography>
        <Typography>査定完了日：{formatDate(result.appraisal_completed_at)}</Typography>
      </div>

      {result.decision_finalized ? (
        <Paper elevation={0} className={classes.completion}>
          <Typography variant="h5" className={classes.completionTitle}>売却内容の回答が完了しました</Typography>
          <Typography>ご回答ありがとうございます。選択された商品の買取手続きを進めます。</Typography>
          {returnCount > 0 && <Typography>返却を選択された商品は、返却手続きを行います。</Typography>}
        </Paper>
      ) : (
        <Typography className={classes.guidance}>
          チェックした商品を売却し、チェックを外した商品は返却します。内容を確認してから回答を確定してください。
        </Typography>
      )}

      <Paper elevation={0} className={classes.summary}>
        <div className={classes.summaryGrid}>
          <div className={classes.summaryItem}><Typography className={classes.summaryLabel}>売却点数</Typography><Typography className={classes.summaryValue}>{totalCount}点</Typography></div>
          <div className={classes.summaryItem}><Typography className={classes.summaryLabel}>返却点数</Typography><Typography className={classes.summaryValue}>{returnCount}点</Typography></div>
          <div className={classes.summaryItem}><Typography className={classes.summaryLabel}>合計買取価格</Typography><Typography className={classes.summaryValue}>{totalPrice.toLocaleString()}円</Typography></div>
        </div>
      </Paper>

      <TableContainer component={Paper} className={classes.desktopTable}>
        <Table aria-label="買取査定明細">
          <TableHead><TableRow><TableCell>売却・返却</TableCell><TableCell>アイテム名</TableCell><TableCell>状態</TableCell><TableCell align="right">買取価格</TableCell><TableCell>備考</TableCell></TableRow></TableHead>
          <TableBody>
            {result.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{renderDecision(item)}</TableCell>
                <TableCell>{item.item}</TableCell>
                <TableCell>{conditionLabel(item.condition)}</TableCell>
                <TableCell align="right">{Number(item.price).toLocaleString()}円</TableCell>
                <TableCell>{item.remarks || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.mobileItems}>
        {result.items.map((item) => (
          <Paper elevation={0} key={item.id} className={`${classes.itemCard} ${decisions[String(item.id)] ? classes.selectedCard : ''}`}>
            <div className={classes.itemHeader}>
              <Typography className={classes.itemName}>{item.item}</Typography>
              <Typography className={classes.itemPrice}>{Number(item.price).toLocaleString()}円</Typography>
            </div>
            <Typography className={classes.itemDetail}>状態：{conditionLabel(item.condition)}</Typography>
            <Typography className={classes.itemDetail}>備考：{item.remarks || 'なし'}</Typography>
            <Divider className={classes.itemDetail} />
            <div className={classes.itemDecision}>{renderDecision(item)}</div>
          </Paper>
        ))}
      </div>

      {error && <Typography color="error" className={classes.message}>{error}</Typography>}
      {!result.decision_finalized && (
        <Button fullWidth variant="contained" color="primary" className={classes.button} disabled={submitting} onClick={() => setConfirmOpen(true)}>
          {submitting ? '確定中...' : 'この内容で売却回答を確定する'}
        </Button>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} aria-labelledby="appraisal-confirm-title">
        <DialogTitle id="appraisal-confirm-title">売却内容を確定しますか？</DialogTitle>
        <DialogContent>
          <DialogContentText>売却{totalCount}点、返却{returnCount}点、合計買取価格{totalPrice.toLocaleString()}円で回答を確定します。確定後は変更できません。</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>内容を見直す</Button>
          <Button onClick={submitDecision} color="primary" variant="contained">回答を確定する</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
